import { Button } from '@/app/components/Button';
import FieldError from '@/app/components/FieldError';
import TextField from '@/app/components/TextField';
import { PATH } from '@/app/constants/path';
import { appClientConfig } from '@/app/utils/appClientConfig';
import { capitaliseFirstChar } from '@/app/utils/capitaliseFirstChar';
import { tryToNoticeClientError } from '@/app/utils/clientErrorInfo';
import yup from '@/app/utils/customisedYup';
import { useAnalytics } from '@/app/utils/useAnalytics';
import { useClientSideUser } from '@/app/utils/useClientSideUser';
import { postLeadforms } from '@/app/utils/useRequest';
import { useSessionStorage } from '@/app/utils/useSessionStorage';
import {
  emailValidator,
  firstNameValidator,
  lastNameValidator,
  permissionValidator,
  phoneNumberValidator,
} from '@/app/utils/validators';
import { useFeedBackOutlet } from '@/modules/Context/FeedbackContextProvider';
import { yupResolver } from '@hookform/resolvers';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { InferType } from 'yup';

import { Checkbox, FormControlLabel } from '@material-ui/core';

import styles from './ReferFriendForm.module.scss';

const referFriendSchema = yup.object().shape({
  email: emailValidator(),
  firstName: firstNameValidator(),
  lastName: lastNameValidator(),
  phoneNumber: phoneNumberValidator(),
  permission: permissionValidator('You must have permission from your friend to refer them.'),
});

type ReferFriendFormData = InferType<typeof referFriendSchema>;

const referFriendFormDefaultValues: ReferFriendFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  permission: false,
};

export const ReferFriendForm = () => {
  const track = useAnalytics();
  const setFeedback = useFeedBackOutlet();
  const { user, userProfile } = useClientSideUser();
  const router = useRouter();
  const [storedFormData, setStoredFormData, removeStoredFormData] =
    useSessionStorage<ReferFriendFormData>('referFriendForm', referFriendFormDefaultValues);

  const { handleSubmit, control, errors, reset } = useForm<ReferFriendFormData>({
    resolver: yupResolver(referFriendSchema),
    reValidateMode: 'onSubmit',
    mode: 'onSubmit',
    defaultValues: storedFormData,
  });

  const clearOnNavigationRef = useRef(true);

  useEffect(() => {
    return () => {
      if (clearOnNavigationRef.current) {
        removeStoredFormData();
      }
    };
  }, [removeStoredFormData]);

  const handleNoUserSignedIn = (data: ReferFriendFormData) => {
    clearOnNavigationRef.current = false;
    setStoredFormData(data);
    const currentUrl = router.asPath;
    const redirectParams = new URLSearchParams();
    redirectParams.append('back_to', 'medrecruit');
    redirectParams.append('redirect_to', currentUrl);
    redirectParams.append('action', 'refer_friend');

    router.push(`${appClientConfig.practitionerProfileUrl}/sign-up?${redirectParams.toString()}`);
    return;
  };

  const onSubmit = async (data: ReferFriendFormData) => {
    if (!user) {
      handleNoUserSignedIn(data);
      return;
    }

    const referralId = userProfile?.affiliateCode;
    if (!referralId) {
      tryToNoticeClientError(new Error(`No referral ID found for user ${user.mr_user_id}`));
      setFeedback({
        errorMessage:
          'Oops, something went wrong. Please try again later. If issues persist please reach out to info@medrecruit.com.',
      });
      return;
    }

    const { firstName, lastName, email, phoneNumber } = data;
    const payload = {
      FirstName: firstName,
      LastName: lastName,
      Email: email,
      Phone: phoneNumber,
      referralId: referralId,
    };
    // format request body i.e. FirstName=Ben&LastName=Smith&Email=email@example.com
    const formBody = new URLSearchParams(payload).toString();

    try {
      await postLeadforms(formBody);
      track('Refer a friend form completed', {});
      setFeedback({
        successMessage: 'Thank you for referring your friend! Refer as many friends as you like.',
      });

      removeStoredFormData();
      reset(referFriendFormDefaultValues);
    } catch (error) {
      tryToNoticeClientError(error);
      setFeedback({ errorMessage: 'Oops, something went wrong. Please try again later.' });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.referFriendForm}>
      <p className={styles.formHeading}>Doctor being referred</p>
      <Controller
        name="firstName"
        control={control}
        defaultValue=""
        render={({ onChange, onBlur, value, name }) => (
          <TextField
            name={name}
            value={value}
            onChange={e => {
              const val = capitaliseFirstChar(e.target.value);
              onChange(val);
            }}
            onBlur={onBlur}
            label="First name"
            error={!!errors.firstName}
            errorMessage={errors.firstName?.message}
            type="text"
          />
        )}
      />

      <Controller
        name="lastName"
        control={control}
        defaultValue=""
        render={({ onChange, onBlur, value, name }) => (
          <TextField
            name={name}
            value={value}
            onChange={e => {
              const val = capitaliseFirstChar(e.target.value);
              onChange(val);
            }}
            onBlur={onBlur}
            label="Last name"
            error={!!errors.lastName}
            errorMessage={errors.lastName?.message}
            type="text"
          />
        )}
      />

      <Controller
        name="email"
        control={control}
        defaultValue=""
        render={({ onChange, onBlur, value, name }) => (
          <TextField
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            label="Email address"
            error={!!errors.email}
            errorMessage={errors.email?.message}
            type="text"
          />
        )}
      />
      <Controller
        name="phoneNumber"
        control={control}
        defaultValue=""
        render={({ onChange, onBlur, value, name }) => (
          <TextField
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            label="Phone number"
            error={!!errors.phoneNumber}
            errorMessage={errors.phoneNumber?.message}
            type="text"
          />
        )}
      />
      {!user && (
        <p className={styles.userSignedInNotice}>
          To refer a friend, you must first <span>sign in</span> or <span>register</span> with
          Medrecruit. Being signed in keeps your data safe.
        </p>
      )}
      <Controller
        name="permission"
        control={control}
        defaultValue={false}
        render={({ onChange, value }) => (
          <>
            <FormControlLabel
              className={styles.permissionCheckbox}
              control={
                <Checkbox
                  checked={value}
                  onChange={() => onChange(!value)}
                  style={{
                    color: value ? '#0f68ef' : '#889096',
                  }}
                />
              }
              classes={{
                label: styles.permissionLabel,
              }}
              label="I confirm that I have my friend’s consent to share their details with Medrecruit."
            />
            {errors.permission && <FieldError errorMessage={errors.permission?.message} />}
          </>
        )}
      />

      <div className={styles.submitPlusTsAndCsContainer}>
        <Button
          type="submit"
          trackFunction={eventProps => {
            track('Click', {
              ...eventProps,
              action: 'Refer Friend',
            });
          }}
        >
          Refer a friend now
        </Button>
        <Link href={PATH.TERMS_AND_CONDITIONS} passHref>
          <a className={styles.termsAndConditionsLink}>*T&Cs apply</a>
        </Link>
      </div>
    </form>
  );
};
